import React, { useState, useEffect } from "react";
import * as d3 from "d3";
import "./DonutChart.scss";

interface DonutDatum {
  label: string;
  percentage: number;
  color: string;
}

interface DonutChartProps {
  data: DonutDatum[];
  title: string;
  subtitle: string;
  width?: number;
  height?: number;
}

const DonutChart: React.FC<DonutChartProps> = ({
  data,
  title,
  subtitle,
  width: defaultWidth = 600,
  height: defaultHeight = 400,
}) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 480);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Use smaller dimensions on mobile
  const width = isMobile ? 380 : defaultWidth;
  const height = isMobile ? 380 : defaultHeight;

  const radius = Math.min(width, height) / 2 - (isMobile ? 100 : 80);
  const innerRadius = radius * 0.7; // donut hole - made thinner

  const pieGen = d3.pie<DonutDatum>().value((d) => d.percentage);
  const arcGen = d3
    .arc<d3.PieArcDatum<DonutDatum>>()
    .innerRadius(innerRadius)
    .outerRadius(radius);

  const gapSize = 0.02;

  return (
    <div className="donut-chart">
      <div className="chart-header">
        <h3 className="chart-title">{title}</h3>
        <p className="chart-subtitle">{subtitle}</p>
      </div>

      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="xMidYMid meet"
        className="donut-svg"
      >
        <g transform={`translate(${width / 2},${height / 2})`}>
          {pieGen(data).map((arc, i) => {
            // Create gap by adjusting start and end angles
            const adjustedStartAngle = arc.startAngle + gapSize / 2;
            const adjustedEndAngle = arc.endAngle - gapSize / 2;

            // Create adjusted arc data
            const adjustedArc = {
              ...arc,
              startAngle: adjustedStartAngle,
              endAngle: adjustedEndAngle,
            };

            const angle = (adjustedStartAngle + adjustedEndAngle) / 2;
            const labelRadius = radius + (isMobile ? 40 : 30);
            const labelX = Math.cos(angle - Math.PI / 2) * labelRadius;
            const labelY = Math.sin(angle - Math.PI / 2) * labelRadius;

            // line from arc edge to label
            const lineX = Math.cos(angle - Math.PI / 2) * (radius + 5);
            const lineY = Math.sin(angle - Math.PI / 2) * (radius + 5);

            return (
              <g key={arc.data.label}>
                <path
                  d={arcGen(adjustedArc) ?? undefined}
                  fill={arc.data.color}
                  stroke="white"
                  strokeWidth={1}
                />

                {/* line from arc edge to label */}
                <line
                  x1={lineX}
                  y1={lineY}
                  x2={labelX}
                  y2={labelY}
                  stroke="#000"
                  strokeWidth={1}
                />

                {/* label */}
                <text
                  x={labelX + (labelX < 0 ? -15 : 15)}
                  y={labelY + (labelY < 0 ? -5 : 5)}
                  textAnchor={labelX > 0 ? "start" : "end"}
                  alignmentBaseline="middle"
                  fontSize={isMobile ? 10 : 14}
                  fill="#333"
                  style={{ fontWeight: 500 }}
                >
                  {(() => {
                    const words = arc.data.label.split(" ");
                    const tspans = [];
                    let lineCount = 0;
                    const lineHeight = isMobile ? 12 : 16;

                    // Split label into lines of max 2-3 words each
                    for (let index = 0; index < words.length; index += 3) {
                      const lineWords = words.slice(index, index + 3);
                      tspans.push(
                        <tspan
                          key={index}
                          x={labelX + (labelX < 0 ? -10 : 10)}
                          dy={lineCount === 0 ? 0 : lineHeight}
                        >
                          {lineWords.join(" ")}
                        </tspan>
                      );
                      lineCount++;
                    }

                    // Add percentage on a new line
                    tspans.push(
                      <tspan
                        key="percentage"
                        x={labelX + (labelX < 0 ? -10 : 10)}
                        dy={lineHeight}
                      >
                        ({arc.data.percentage}%)
                      </tspan>
                    );

                    return tspans;
                  })()}
                </text>
              </g>
            );
          })}
        </g>
      </svg>
    </div>
  );
};

export default DonutChart;
